import {
  Button,
  FormGroup,
  Icon,
  InputGroup,
  Spinner,
} from "@blueprintjs/core";
import {
  ITEM_GRID_QUERY_SEARCH_DEBOUNCE_MS,
  ItemGridPagination,
  MediaImage,
} from "@package/ui-apollo-components";
import {
  useGetMediaGridQuery,
  type GetMediaGridQuery,
} from "@package/ui-gql/index-data-access";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { SidebarEntryPath } from "../../../../constants/contants";
import {
  setItemGridPage,
  setItemGridPerPage,
  setItemGridQuery,
  setItemGridSelectedItemId,
} from "../../../../store/slices/slice-item-grid";
import { setSelectedParentIdHierarchy } from "../../../../store/slices/slice-media";
import { useAppDispatch, useAppSelector } from "../../../../store/store";

type MediaTilePanelItemProps = {
  media?: GetMediaGridQuery["items"][0];
  onClick: () => void;
};

function MediaTilePanelItem({ media, onClick }: MediaTilePanelItemProps) {
  const selectedItemId = useAppSelector(
    (state) => state.itemGrid.media.selectedItemId,
  );

  return (
    <div className="flex flex-col mr-2 mb-2">
      <div
        className={`flex flex-col h-52 w-52 p-2 items-center justify-center rounded ${selectedItemId === media?.id ? "bg-sky-200" : "bg-slate-200"}`}
        onClick={onClick}
      >
        <MediaImage id={media?.id ?? undefined} size="max-h-48" />
      </div>
      <p className="overflow-hidden text-center text-ellipsis w-52 whitespace-nowrap">
        {media?.name}
      </p>
    </div>
  );
}

export function MediaTilePanel() {
  const dispatch = useAppDispatch();
  const selectedParentIdHierarchy = useAppSelector(
    (state) => state.media.selectedParentIdHierarchy,
  );
  const selectedItemId = useAppSelector(
    (state) => state.itemGrid.media.selectedItemId,
  );
  const variables = useAppSelector((state) => state.itemGrid.media.variables);
  const page = useAppSelector(
    (state) => state.itemGrid.media.variables.page ?? 0,
  );
  const perPage = useAppSelector(
    (state) => state.itemGrid.media.variables.perPage ?? 10,
  );
  const q = useAppSelector(
    (state) => state.itemGrid.media.variables.filter?.q ?? "",
  );

  const { data: dataMediaRoot } = useGetMediaGridQuery({
    variables: {
      filter: {
        isDir: true,
        root: true,
      },
    },
  });

  const [localSearchValue, setLocalSearchValue] = useState<string>(q);

  const debouncedUpdateQuery = useRef(
    debounce((query: string, rootId?: string) => {
      if (query && rootId) {
        dispatch(setSelectedParentIdHierarchy([rootId]));
      }

      dispatch(
        setItemGridQuery({
          path: SidebarEntryPath.media,
          query,
        }),
      );
      dispatch(
        setItemGridPage({
          path: SidebarEntryPath.media,
          page: 0,
        }),
      );
    }, ITEM_GRID_QUERY_SEARCH_DEBOUNCE_MS),
  );

  const onPageChanged = useCallback(
    (p: number) =>
      dispatch(setItemGridPage({ path: SidebarEntryPath.media, page: p })),
    [dispatch],
  );
  const onPerPageChanged = useCallback(
    (p: number) =>
      dispatch(
        setItemGridPerPage({ path: SidebarEntryPath.media, perPage: p }),
      ),
    [dispatch],
  );

  const {
    data: dataMedia,
    loading: loadingMedia,
    error: errorMedia,
  } = useGetMediaGridQuery({
    variables: {
      ...variables,
      filter: {
        ...variables.filter,
        parent: selectedParentIdHierarchy.slice(-1)?.[0],
        q,
      },
    },
    skip: !selectedParentIdHierarchy?.length,
  });

  useEffect(
    function effectOnLocalQueryValueChanged() {
      debouncedUpdateQuery.current(
        localSearchValue,
        dataMediaRoot?.items?.[0]?.id ?? undefined,
      );
    },
    [dataMediaRoot?.items, debouncedUpdateQuery, localSearchValue],
  );

  if (errorMedia) {
    return <Icon icon="warning-sign" title="Failed to load media details" />;
  }

  return (
    <>
      <div className="flex flex-row flex-wrap mt-4">
        <FormGroup fill>
          <InputGroup
            large
            fill
            value={localSearchValue}
            placeholder="Filter for items (case insensitive)"
            inputClassName="placeholder:italic"
            onValueChange={(value) => setLocalSearchValue(value)}
            rightElement={
              <Button
                intent="none"
                icon="cross"
                minimal
                outlined
                small
                text="Clear"
                onClick={() => setLocalSearchValue("")}
              />
            }
          />
        </FormGroup>
        {loadingMedia && <Spinner />}
        {dataMedia?.items?.map((media) => (
          <MediaTilePanelItem
            key={media?.id}
            media={media}
            onClick={() => {
              if (media?.id) {
                if (media?.isDir && media?.id === selectedItemId) {
                  setLocalSearchValue("");

                  dispatch(
                    setSelectedParentIdHierarchy([
                      ...selectedParentIdHierarchy,
                      media.id,
                    ]),
                  );
                } else {
                  dispatch(
                    setItemGridSelectedItemId({
                      path: SidebarEntryPath.media,
                      id: media?.id,
                    }),
                  );
                }
              }
            }}
          />
        ))}
      </div>
      <div className="mt-4">
        <ItemGridPagination
          page={page}
          perPage={perPage}
          onPageChanged={onPageChanged}
          onPerPageChanged={onPerPageChanged}
          totalItems={dataMedia?.total?.count ?? 0}
        />
      </div>
    </>
  );
}
